import { useCallback } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import useApi from './useApi';
import useProfile from './useProfile';
import { useSnapshot } from 'valtio';

/**
 * useListData
 * Centralized data-fetching for list views using a stable queryKey and robust URL construction.
 *
 * Params:
 * - endpoint: string (e.g. 'invoice', 'invoicePayment')
 * - order: [orderBy: string, direction: 'asc' | 'desc']
 * - searchTerm: string (already debounced if you debounce in the controller)
 * - filterTerm: string | false (client-side filter param string, e.g. 'state:final;archived:false')
 * - serverFilter: string | false (server-side filter param string, e.g. 'paymentDate:{today}')
 * - staleTime: number (ms to keep data fresh)
 */
export default function useListData({
  endpoint,
  order,
  searchTerm,
  filterTerm,
  serverFilter,
  conditions,
  staleTime = 0,
  queryParams,
}) {
  // Serialised once so it is stable across renders and usable as a query key.
  const conditionParam = conditions && (!Array.isArray(conditions) || conditions.length)
    ? JSON.stringify(conditions)
    : '';

  const { getMe } = useApi();
  const { pState } = useProfile();
  const snapshot = useSnapshot(pState);

  const getData = useCallback(
    async ({ pageParam }) => {
      const [orderBy, direction] = order || [];
      const params = new URLSearchParams();

      if (orderBy) params.set('orderBy', orderBy);
      if (direction) params.set('direction', direction);

      // Sent as typed: the server reads amounts (`1.234,50`) itself (40.02 §5.1a)
      if (searchTerm && searchTerm.length >= 3) params.set('searchString', searchTerm);

      if (filterTerm) params.set('filter', filterTerm);
      if (serverFilter) params.set('serverFilter', serverFilter);
      if (conditionParam) params.set('conditions', conditionParam);
      if (queryParams) Object.entries(queryParams).forEach(([k, v]) => params.set(k, v));
      if (pageParam) params.set('startAfter', pageParam);

      const resource = `${endpoint}/?${params.toString()}`;
      const response = await getMe(resource);
      return response;
    },
    [endpoint, getMe, order, searchTerm, filterTerm, serverFilter, conditionParam, queryParams]
  );

  return useInfiniteQuery({
    queryKey: [
      'list',
      endpoint,
      order?.[0] || '',
      order?.[1] || '',
      searchTerm || '',
      filterTerm || '',
      serverFilter || '',
      conditionParam,
      queryParams ? JSON.stringify(queryParams) : '',
    ],
    queryFn: ({ pageParam }) => getData({ pageParam }),
    keepPreviousData: true,
    staleTime,
    getNextPageParam: (lastPage) => {
      if (!lastPage) return null;
      // A search answer is one ranked page (40.02 §5.2) — there is no next page to fetch
      if (lastPage?.meta?.searchMode) return null;
      // Respect the global pagination size from profile state
      if (lastPage?.meta?.size < snapshot.pagination) return null;
      return lastPage?.meta?.lastId;
    },
  });
}
